import type { JsonValue } from "@e308/core";
import { BUILDINGS, type BuildingId, UNITS, type UnitId } from "./content/buildings.js";
import { LIMITS } from "./content/setup.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import {
  type ManufacturingOrder,
  type ManufacturingState,
  QUEUE_KINDS,
  type QueueKind,
} from "./manufacturing-types.js";
import { exactRecordKeys as exactKeys, recordObject as objectRecord } from "./record-validation.js";

const ORDER_ID = /^order-[1-9][0-9]*$/;
const INITIAL: ManufacturingState = {
  nextOrderId: 3,
  construction: [order("order-1", "mining-rig", "1", true)],
  restoration: [],
  assembly: [order("order-2", "guard-robot", "1", true)],
  paused: { construction: false, restoration: false, assembly: false },
  factoryWorksRetoolUntilMs: 0,
};

export function initialManufacturingState(): ManufacturingState & JsonValue {
  return cloneState(INITIAL) as ManufacturingState & JsonValue;
}

export function validateManufacturingState(value: unknown): ManufacturingState & JsonValue {
  const source = objectRecord(value, "manufacturing state");
  exactKeys(source, [
    "nextOrderId",
    "construction",
    "restoration",
    "assembly",
    "paused",
    "factoryWorksRetoolUntilMs",
  ]);
  if (!Number.isSafeInteger(source.nextOrderId) || (source.nextOrderId as number) < 1) {
    throw new TypeError("Invalid next order ID");
  }
  if (
    !Number.isSafeInteger(source.factoryWorksRetoolUntilMs) ||
    (source.factoryWorksRetoolUntilMs as number) < 0
  ) {
    throw new TypeError("Invalid Factory Works retool deadline");
  }
  const paused = objectRecord(source.paused, "paused queues");
  exactKeys(paused, QUEUE_KINDS);
  for (const kind of QUEUE_KINDS) {
    if (typeof paused[kind] !== "boolean") throw new TypeError(`Invalid ${kind} pause state`);
  }
  const state: ManufacturingState = {
    nextOrderId: source.nextOrderId as number,
    construction: validateQueue(source.construction, "construction"),
    restoration: validateQueue(source.restoration, "restoration"),
    assembly: validateQueue(source.assembly, "assembly"),
    paused: paused as unknown as ManufacturingState["paused"],
    factoryWorksRetoolUntilMs: source.factoryWorksRetoolUntilMs as number,
  };
  const ids = [...state.construction, ...state.restoration, ...state.assembly].map(
    (entry) => entry.id,
  );
  if (new Set(ids).size !== ids.length) throw new TypeError("Queue order IDs must be unique");
  const greatestId = ids.reduce((greatest, id) => Math.max(greatest, Number(id.slice(6))), 0);
  if (state.nextOrderId <= greatestId) throw new TypeError("Next order ID must exceed queued IDs");
  return cloneState(state) as ManufacturingState & JsonValue;
}

export function enqueueOrder(
  state: ManufacturingState,
  queue: QueueKind,
  target: string,
  quantity: string,
  repeat: boolean,
): ManufacturingState & JsonValue {
  validateTarget(queue, target);
  const canonical = positiveQuantity(quantity);
  if (state[queue].length >= LIMITS[queueLimit(queue)]) {
    throw new TypeError(`${queue} queue is limited to ${LIMITS[queueLimit(queue)]} orders`);
  }
  const id = `order-${state.nextOrderId}`;
  return cloneState({
    ...state,
    nextOrderId: state.nextOrderId + 1,
    [queue]: [...state[queue], order(id, target as BuildingId | UnitId, canonical, repeat)],
  }) as ManufacturingState & JsonValue;
}

export function cancelOrder(
  state: ManufacturingState,
  queue: QueueKind,
  orderId: string,
): ManufacturingState & JsonValue {
  requiredOrder(state, queue, orderId);
  return cloneState({
    ...state,
    [queue]: state[queue].filter((entry) => entry.id !== orderId),
  }) as ManufacturingState & JsonValue;
}

export function setOrderRepeat(
  state: ManufacturingState,
  queue: QueueKind,
  orderId: string,
  repeat: boolean,
): ManufacturingState & JsonValue {
  requiredOrder(state, queue, orderId);
  return cloneState({
    ...state,
    [queue]: state[queue].map((entry) => (entry.id === orderId ? { ...entry, repeat } : entry)),
  }) as ManufacturingState & JsonValue;
}

export function reorderQueue(
  state: ManufacturingState,
  queue: QueueKind,
  orderIds: readonly string[],
): ManufacturingState & JsonValue {
  const current = state[queue];
  if (
    orderIds.length !== current.length ||
    new Set(orderIds).size !== current.length ||
    orderIds.some((id) => !current.some((entry) => entry.id === id))
  ) {
    throw new TypeError("Queue reorder must contain every current order exactly once");
  }
  const byId = new Map(current.map((entry) => [entry.id, entry]));
  return cloneState({
    ...state,
    [queue]: orderIds.map((id) => byId.get(id) as ManufacturingOrder),
  }) as ManufacturingState & JsonValue;
}

export function setQueuePaused(
  state: ManufacturingState,
  queue: QueueKind,
  paused: boolean,
): ManufacturingState & JsonValue {
  return cloneState({
    ...state,
    paused: { ...state.paused, [queue]: paused },
  }) as ManufacturingState & JsonValue;
}

export function advanceQueueHead(
  state: ManufacturingState,
  queue: QueueKind,
  completed: Micro,
): ManufacturingState & JsonValue {
  if (completed <= 0n || state[queue].length === 0) return state as ManufacturingState & JsonValue;
  const head = state[queue][0] as ManufacturingOrder;
  const remaining = microFromString(head.remaining);
  if (completed > remaining) throw new TypeError("Queue progress exceeds the head remainder");
  if (completed < remaining) {
    return cloneState({
      ...state,
      [queue]: [
        { ...head, remaining: formatMicro((remaining - completed) as Micro) },
        ...state[queue].slice(1),
      ],
    }) as ManufacturingState & JsonValue;
  }
  const tail = state[queue].slice(1);
  return cloneState({
    ...state,
    [queue]: head.repeat ? [...tail, { ...head, remaining: head.quantity }] : tail,
  }) as ManufacturingState & JsonValue;
}

export function withRetoolDeadline(
  state: ManufacturingState,
  deadlineMs: number,
): ManufacturingState & JsonValue {
  return cloneState({ ...state, factoryWorksRetoolUntilMs: deadlineMs }) as ManufacturingState &
    JsonValue;
}

function validateQueue(value: unknown, queue: QueueKind): readonly ManufacturingOrder[] {
  if (!Array.isArray(value) || value.length > LIMITS[queueLimit(queue)]) {
    throw new TypeError(`Invalid ${queue} queue`);
  }
  return Object.freeze(value.map((entry) => validateOrder(entry, queue)));
}

function validateOrder(value: unknown, queue: QueueKind): ManufacturingOrder {
  const source = objectRecord(value, `${queue} order`);
  exactKeys(source, ["id", "target", "quantity", "remaining", "repeat"]);
  if (typeof source.id !== "string" || !ORDER_ID.test(source.id))
    throw new TypeError("Invalid order ID");
  if (typeof source.target !== "string") throw new TypeError("Invalid order target");
  validateTarget(queue, source.target);
  const quantity = positiveQuantity(source.quantity);
  const remaining = positiveQuantity(source.remaining);
  if (microFromString(remaining) > microFromString(quantity))
    throw new TypeError("Order remainder exceeds quantity");
  if (typeof source.repeat !== "boolean") throw new TypeError("Invalid Repeat value");
  return order(source.id, source.target as BuildingId | UnitId, quantity, source.repeat, remaining);
}

function validateTarget(queue: QueueKind, target: string): void {
  const ids =
    queue === "assembly" ? UNITS.map((entry) => entry.id) : BUILDINGS.map((entry) => entry.id);
  if (!ids.includes(target as never)) throw new TypeError(`${target} is invalid for ${queue}`);
}

function positiveQuantity(value: unknown): string {
  if (typeof value !== "string") throw new TypeError("Quantity must be decimal text");
  const parsed = microFromString(value);
  if (parsed <= 0n) throw new TypeError("Quantity must be positive");
  return formatMicro(parsed);
}

function requiredOrder(
  state: ManufacturingState,
  queue: QueueKind,
  id: string,
): ManufacturingOrder {
  const value = state[queue].find((entry) => entry.id === id);
  if (!value) throw new TypeError(`Unknown ${queue} order: ${id}`);
  return value;
}

function queueLimit(queue: QueueKind): "constructionQueue" | "restorationQueue" {
  return queue === "restoration" ? "restorationQueue" : "constructionQueue";
}

function order(
  id: string,
  target: BuildingId | UnitId,
  quantity: string,
  repeat: boolean,
  remaining = quantity,
): ManufacturingOrder {
  return Object.freeze({ id, target, quantity, remaining, repeat });
}

function cloneState(state: ManufacturingState): ManufacturingState {
  return Object.freeze({
    ...state,
    construction: Object.freeze(state.construction.map((entry) => Object.freeze({ ...entry }))),
    restoration: Object.freeze(state.restoration.map((entry) => Object.freeze({ ...entry }))),
    assembly: Object.freeze(state.assembly.map((entry) => Object.freeze({ ...entry }))),
    paused: Object.freeze({ ...state.paused }),
  });
}
